/**
 * Data object to populate the marketing tab.
 */
import {MarketingConfig, UNITS_UNIT_OF_MEASURE_CODE} from "./MarketingConfig";

export namespace MarketingData {

    import UnitOfMeasure = MarketingConfig.UnitOfMeasure;

    export interface PeriodData {
        assets: Assets;
        bestLocations: LocationTrendRow[];
        productSales: ProductSales;
        dayIntervalSales: DayIntervalSaleRow[];
        weekdaySales: WeekdaySaleRow[];
        sales: number;
        salesYearAgo: number;
        salesTwoYearsAgo: number;
        salesLocationAverages: number;
        salesSelectionAverages: number;
        salesEquipmentAverages: number;
        salesLocationAveragesYearAgo: number;
        salesEquipmentAveragesYearAgo: number;
        salesSelectionAveragesYearAgo: number;
        consumptionsSelectionAveragesYearAgo: number;
        consumptionsEquipmentAveragesYearAgo: number;
        consumptionsLocationAveragesYearAgo: number;
        salesLocationAveragesTwoYearsAgo: number;
        salesEquipmentAveragesTwoYearsAgo: number;
        salesSelectionAveragesTwoYearsAgo: number;
        consumptionsSelectionAveragesTwoYearsAgo: number;
        consumptionsEquipmentAveragesTwoYearsAgo: number;
        consumptionsLocationAveragesTwoYearsAgo: number;
        consumptionsEquipmentAverages: number;
        consumptionsSelectionAverages: number;
        consumptionsLocationAverages: number;
        consumptionsYearAgo: number;
        consumptionsTwoYearsAgo: number;
        consumptions: number;
        productSelectionsAverages: ProductSelectionsAverages[];
        weekdayDayIntervalSales: WeekdayDayIntervalSale[];
    }

    export interface RollingPeriodWithWeeklySales {
        assets: Assets;
        bestLocations: LocationTrendRow[];
        productSales: ProductSales;
        dayIntervalSales: DayIntervalSaleRow[];
        weekdaySales: WeekdaySaleRow[];
        weekSales: WeekSaleRow[];
        sales: number;
        salesLocationAverages: number;
        salesSelectionAverages: number;
        salesEquipmentAverages: number;
        consumptions: number;
        consumptionsLocationAverages: number;
        consumptionsSelectionAverages: number;
        consumptionsEquipmentAverages: number;
        productSelectionsAverages: ProductSelectionsAverages[];
        weekdayDayIntervalSales: WeekdayDayIntervalSale[];
        assetsAverages: AssetsAverageRow[];
    }

    export interface RollingPeriodWithMonthlySales {
        assets: Assets;
        bestLocations: LocationTrendRow[];
        productSales: ProductSales;
        dayIntervalSales: DayIntervalSaleRow[];
        weekdaySales: WeekdaySaleRow[];
        monthSales: MonthSaleRow[];
        sales: number;
        salesLocationAverages: number;
        salesSelectionAverages: number;
        salesEquipmentAverages: number;
        consumptions: number;
        consumptionsLocationAverages: number;
        consumptionsSelectionAverages: number;
        consumptionsEquipmentAverages: number;
        productSelectionsAverages: ProductSelectionsAverages[];
        weekdayDayIntervalSales: WeekdayDayIntervalSale[];
        assetsAverages: AssetsAverageRow[];
    }

    export interface BarChartGroupElement {
        name: string;
        color: string;
        value: number;
    }

    export interface BarChartGroup {
        name: string;
        elements: BarChartGroupElement[];
    }

    export interface WeekdayDayIntervalSale {
        weekDay: number;
        name: string;
        dayIntervalSaleRows: DayIntervalSaleRow[];
    }

    export interface Assets {
        locations: number;
        telemetries: number;
        selections: number;
    }

    export interface LocationTrendRow {
        id: number;
        code: string;
        description: string;
        total: number;
        totalName: string;
        totalColor: string;
        comparativeTotal: number;
        comparativeTotalName: string;
        comparativeTotalColor: string;
        secondComparativeTotal: number;
        secondComparativeTotalName: string;
        secondComparativeTotalColor: string;
    }

    export interface BestProductSale {
        productName: string;
        percentage: number;
        value: number;
    }

    export interface ProductSales {
        bestProductByQuantity: BestProductSale;
        bestProductByConsumption: BestProductSale;
        productSaleRows: ProductSaleRow[];
    }

    export interface ProductSaleRow {
        productId: number;
        name: string;
        color: string;
        date: number;
        quantity: number;
        consumptions: number;
    }

    export interface DayIntervalSaleRow {
        dayIntervalCode: number;
        name: string;
        color: string;
        date: number;
        quantity: number;
        consumptions: number;
    }

    export interface WeekdaySaleRow {
        weekDay: number;
        name: string;
        color: string;
        date: number;
        quantity: number;
        consumptions: number;
    }

    export interface WeekSaleRow {
        week: number;
        name: string;
        date: number;
        quantity: number;
        consumptions: number;
        selections: number;
    }

    export interface MonthSaleRow {
        month: number;
        name: string;
        date: number;
        quantity: number;
        consumptions: number;
        selections: number;
    }

    export interface ProductSelectionsAverages {
        productId: number;
        name: string;
        color: string;
        selections: number;
        selectionAverage: number;
        selectionConsumptionsAverage: number;
    }

    export interface AssetsAverageRow {
        date: number;
        name: string;
        locationQuantity: number;
        equipmentQuantity: number;
        selectionQuantity: number;
        locationConsumptions: number;
        equipmentConsumptions: number;
        selectionConsumptions: number;
    }

    export class MarketingDataConverter {
        public static convert(quantity: number, unitOfMeasure: UnitOfMeasure): number {
            if (quantity == null) {
                return 0;
            }
            return quantity * unitOfMeasure.ratio;
        }

        public static chooseValue(quantityValue: number, consumptionValue: number, unitOfMeasure: UnitOfMeasure) {
            if (MarketingDataConverter.isUnitsUnitOfMeasure(unitOfMeasure)) {
                return consumptionValue;
            }
            return quantityValue;
        }

        public static chooseAndConvertValue(
            quantityValue: number,
            consumptionValue: number,
            unitOfMeasure: UnitOfMeasure,
        ) {
            return MarketingDataConverter.convert(
                MarketingDataConverter.chooseValue(
                    quantityValue,
                    consumptionValue,
                    unitOfMeasure,
                ),
                unitOfMeasure,
            );
        }
        public static isUnitsUnitOfMeasure(unitOfMeasure: UnitOfMeasure) {
            return unitOfMeasure != null && unitOfMeasure.code === UNITS_UNIT_OF_MEASURE_CODE;
        }
    }

}
